import { useState, useEffect, useCallback, useMemo } from 'react';
import { useColorMode } from '@chakra-ui/react';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { 
  loadAllSettings, 
  saveSetting, 
  resetAllSettings, 
  isLocalStorageAvailable,
  type Settings 
} from '@/utils/settingsManager';

export const useSettings = () => {
  const { setColorMode } = useColorMode();
  const toast = useToast();
  const { t } = useTranslation('settings');
  
  const [settings, setSettings] = useState<Settings>({
    theme: 'system',
    language: 'zh',
    notifications: true,
    priceAlerts: true,
    inviteUpdates: true,
  });
  const [savedSettings, setSavedSettings] = useState<Settings>({
    theme: 'system',
    language: 'zh',
    notifications: true,
    priceAlerts: true,
    inviteUpdates: true,
  });
  const [isLoading, setIsLoading] = useState(true);

  // 加载设置
  const loadSettings = useCallback(async () => {
    try {
      const loadedSettings = loadAllSettings();
      setSettings(loadedSettings);
      setSavedSettings(loadedSettings);
    } catch (error) {
      // Failed to load settings
      toast({
        title: t('settingsSaveFailed'),
        description: 'Failed to load saved settings',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast, t]);

  // 更新设置（不自动保存）
  const updateSetting = useCallback(<K extends keyof Settings>(key: K, value: Settings[K]) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    
    // 特殊处理
    if (key === 'theme') {
      const themeValue = value as Settings['theme'];
      if (themeValue === 'system') {
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        setColorMode(systemTheme);
      } else {
        setColorMode(themeValue);
      }
    }
    
    if (key === 'notifications' && value === true) {
      // 请求通知权限
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }
  }, [settings, setColorMode]);

  // 保存所有设置
  const saveAllSettings = useCallback(() => {
    try {
      // 保存每个设置到localStorage
      Object.entries(settings).forEach(([key, value]) => {
        saveSetting(key as keyof Settings, value);
      });
      
      // 更新已保存的设置状态
      setSavedSettings(settings);
      
      toast({
        title: t('settingsSaved'),
        description: 'All settings have been saved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      return true;
    } catch (error) {
      // Failed to save settings
      toast({
        title: t('settingsSaveFailed'),
        description: 'Failed to save settings to local storage',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
  }, [settings, toast, t]);

  // 检查是否有未保存的更改
  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(settings) !== JSON.stringify(savedSettings);
  }, [settings, savedSettings]);

  // 重置设置
  const resetSettings = useCallback(() => {
    resetAllSettings();
    setSettings({
      theme: 'system',
      language: 'en',
      notifications: true,
      priceAlerts: true,
      inviteUpdates: true,
    });
    setColorMode('light');
    toast({
      title: t('resetSettingsTitle'),
      description: 'All settings have been reset to defaults',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  }, [setColorMode, toast, t]);

  // 检查存储状态
  const storageAvailable = isLocalStorageAvailable();

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  return {
    settings,
    isLoading,
    updateSetting,
    saveAllSettings,
    resetSettings,
    storageAvailable,
    loadSettings,
    hasUnsavedChanges,
  };
}; 